import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, Download } from 'lucide-react';

const tiers = [
  {
    name: 'Hobbyist',
    price: 'Free',
    period: '',
    devices: '3 devices',
    description: 'For individual developers and students.',
    features: [
      'Forge Studio Desktop',
      'One-Click ROS2 Setup',
      'Visual Workspace Manager',
      'Package Marketplace',
      'Docker Environments',
      'Community support',
    ],
    cta: 'Download Free',
    ctaLink: '/download',
    featured: false,
  },
  {
    name: 'Pro',
    price: '₹299',
    period: '/month',
    devices: '10 devices',
    description: 'For professional developers and small teams.',
    features: [
      'Everything in Hobbyist',
      'Forge Cloud dashboard',
      'Remote telemetry',
      'Priority support',
      'Advanced network tools',
      'OTA update management',
    ],
    cta: 'Coming Soon',
    ctaLink: null,
    featured: true,
  },
  {
    name: 'Business',
    price: '₹999',
    period: '/month',
    devices: '50 devices',
    description: 'For teams managing robot fleets.',
    features: [
      'Everything in Pro',
      'Fleet management',
      'Team collaboration',
      'Custom deployment pipelines',
      'Analytics dashboard',
      'Dedicated support',
    ],
    cta: 'Coming Soon',
    ctaLink: null,
    featured: false,
  },
];

const Pricing = () => {
  return (
    <div className="min-h-screen bg-page">
      <Header />

      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground-primary mb-6">Simple Pricing</h1>
          <p className="text-xl text-foreground-secondary">
            Start free. Scale when you need to.
          </p>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {tiers.map((tier, i) => (
            <div
              key={i}
              className={`glass-frosted rounded-3xl p-8 transition-smooth glow-subtle relative ${
                tier.featured ? 'ring-2 ring-orange-500' : ''
              }`}
            >
              {tier.featured && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
                  style={{ background: '#F97316', color: '#0B0E13' }}
                >
                  RECOMMENDED
                </div>
              )}

              <div className="text-center space-y-4 mb-8">
                <h3 className="text-2xl font-bold text-foreground-primary">{tier.name}</h3>
                <div>
                  <span className="text-4xl font-bold" style={{ color: '#F97316' }}>{tier.price}</span>
                  {tier.period && <span className="text-foreground-muted">{tier.period}</span>}
                </div>
                <p className="text-xs text-foreground-muted">Up to {tier.devices} (including host)</p>
                <p className="text-sm text-foreground-secondary">{tier.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check size={16} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                    <span className="text-sm text-foreground-secondary">{f}</span>
                  </li>
                ))}
              </ul>

              {tier.ctaLink ? (
                <Link
                  to={tier.ctaLink}
                  className="block w-full py-3 rounded-lg text-center font-semibold transition-smooth"
                  style={{ background: '#F97316', color: '#0B0E13' }}
                >
                  <span className="inline-flex items-center gap-2"><Download size={16} /> {tier.cta}</span>
                </Link>
              ) : (
                <button
                  disabled
                  className="block w-full py-3 rounded-lg text-center font-semibold text-foreground-muted border border-subtle"
                >
                  {tier.cta}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="glass-frosted rounded-3xl p-10 glow-subtle text-center">
            <h2 className="text-3xl font-bold text-foreground-primary mb-4">Enterprise</h2>
            <p className="text-foreground-secondary mb-6">
              Custom device limits, SLA-backed support, annual contracts, and dedicated onboarding for large-scale fleet operations.
            </p>
            <a
              href="https://vinayakrobotics.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
              style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
